import { Text } from '@react-three/drei';
import { noteColors, scaleDegreeColors } from './noteColors';
import { motion } from 'framer-motion-3d';
import { Color } from 'three';

interface NoteBaseProps {
    noteName: string;
    position: [number, number, number];
    isInScale: boolean;
    isChordEnabled: boolean;
    isInChord: boolean;
    isFrettable: boolean;
    rotation: number;
    disabledNoteColor: Color;
    noteOutlineColor: Color;
    noteViewMode: 'letters' | 'scaleDegrees';
    scaleDegree: number | null;
    isChordDetectionMode?: boolean;
    isSelected?: boolean;
    onClick?: () => void;
    showOctave?: boolean;
    octave?: number;
}

export const NoteBase: React.FC<NoteBaseProps> = ({ 
    noteName, 
    position, 
    isInScale, 
    isChordEnabled, 
    isInChord, 
    isFrettable, 
    rotation, 
    disabledNoteColor, 
    noteOutlineColor, 
    noteViewMode, 
    scaleDegree,
    isChordDetectionMode,
    isSelected,
    onClick,
    showOctave,
    octave
}) => {
    const displayNote = noteViewMode === 'scaleDegrees' ? scaleDegree : noteName;
    const noteColor = noteViewMode === 'scaleDegrees' && scaleDegree !== null
        ? scaleDegreeColors[scaleDegree as keyof typeof scaleDegreeColors]
        : noteColors[noteName as keyof typeof noteColors] || '#FFFFFF';

    const handleClick = (e: any) => {
        e.stopPropagation();
        if (isChordDetectionMode && onClick) {
            onClick();
        }
    };

    // Different animation and color settings for selected notes vs chord notes
    const isActive = isInScale || (isChordEnabled && isInChord) || isSelected;
    const isSelectedNote = isChordDetectionMode && isSelected;
    const isChordNote = isChordEnabled && isInChord;

    return (
        <motion.group
            key={position.join('-')}
            position={position}
            initial={{ scale: 0 }}
            animate={{ 
                scale: isActive ? 1 : 0,
                rotateZ: isSelectedNote ? [0, 0.1, 0, -0.1, 0] : 0
            }}
            transition={{ 
                duration: 0.5, 
                ease: [0.68, -0.55, 0.27, 1.55],
                rotateZ: {
                    duration: 0.5,
                    repeat: isSelectedNote ? Infinity : 0,
                    repeatType: "reverse"
                }
            }}
            onClick={handleClick}
        >
            <motion.group
                initial={{ scale: 0 }}
                animate={{ 
                    scale: isSelectedNote ? 1.1 : (isChordNote ? 1 : 0.828)
                }}
                transition={{ duration: 0.25, ease: [0.68, -0.55, 0.27, 1.55] }}
            >
                {(!isFrettable || isSelectedNote) ? <mesh position={[0, 0, 0.2]}>
                    <circleGeometry args={[0.7, 32]} />
                    <motion.meshBasicMaterial
                        animate={{ 
                            opacity: isSelectedNote || isChordNote ? 1 : 0,
                            color: isSelectedNote 
                                ? "#FF0000" // Red color for selected notes
                                : isChordNote 
                                    ? `#${noteOutlineColor.getHexString()}`
                                    : "#3f4b5e"
                        }}
                        transition={{ duration: 0.5 }}
                        transparent={true}
                    />
                </mesh> : null}
            </motion.group>

            <mesh position={[0, 0, 0.3]}>
                <circleGeometry args={[0.58, 32]} />
                <motion.meshBasicMaterial
                    animate={{
                        opacity: 1,
                        color: isChordNote
                            ? (isInScale ? noteColor : `#${disabledNoteColor.getHexString()}`)
                            : (!isChordEnabled ? noteColor : `#${disabledNoteColor.getHexString()}`)
                        }}
                    transition={{ duration: 0.5 }}
                    transparent={true}
                />
            </mesh>

            <Text
                position={[0, 0, 0.5]}
                fontSize={showOctave ? 0.5 : 0.68}
                color={(isChordNote && isInScale) || !isChordEnabled ? "#000000" : "#595959"}
                anchorX="center"
                anchorY="middle"
                rotation={[0, 0, -rotation]}
            >
                {noteViewMode === 'letters' 
                    ? `${noteName}${showOctave ? octave : ''}`
                    : scaleDegree}
            </Text>
        </motion.group>
    );
};
